import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { AppShowcase } from './components/AppShowcase';
import { CTA } from './components/CTA';
import { Footer } from './components/Footer';
import { DownloadPage } from './components/DownloadPage';
import { FeaturesPage } from './components/FeaturesPage';
import { PrivacyPage } from './components/PrivacyPage';
import { PricingPage } from './components/PricingPage';
import { TermsPage } from './components/TermsPage';
import { ContactPage } from './components/ContactPage';
import { CookieBanner } from './components/CookieBanner';
import { AnalyticsLoader } from './components/AnalyticsLoader';
import { resolveCookiePolicyPdfPath } from './legalLinks';
import { useLanguageRouting } from './hooks/useLanguageRouting';
import { useGeolocation } from './hooks/useGeolocation';
import { useCookieConsent } from './hooks/useCookieConsent';
import { parsePath, type Route } from './routing';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface AppProps {
  initialRoute?: Route;
}

function resolveRoute(initialRoute?: Route): Route {
  if (initialRoute) return initialRoute;
  if (typeof window === 'undefined') return 'home';
  return parsePath(window.location.pathname).route;
}

function HomePage() {
  return (
    <>
      <Hero />
      <AppShowcase />
      <CTA />
    </>
  );
}

export default function App({ initialRoute }: AppProps) {
  const route = resolveRoute(initialRoute);
  useLanguageRouting();
  const { t, i18n } = useTranslation();
  const { isEU, loading } = useGeolocation();
  const { shouldShow, analyticsAllowed, accept, reject } = useCookieConsent(loading ? null : isEU);
  const cookiePolicyHref = resolveCookiePolicyPdfPath(i18n.language);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const brand = t('hero.title');
    const tagline = t('hero.subtitle');
    let title = `${brand} - ${tagline}`;

    if (route === 'download') {
      title = `${t('download.pageTitle')} - ${brand}`;
    } else if (route === 'features') {
      title = `${t('nav.features')} - ${brand}`;
    } else if (route === 'pricing') {
      title = `${t('nav.pricing')} - ${brand}`;
    } else if (route === 'privacy') {
      title = `${t('legal.privacy.title')} - ${brand}`;
    } else if (route === 'terms') {
      title = `${t('legal.terms.title')} - ${brand}`;
    } else if (route === 'contact') {
      title = `${t('legal.contact.title')} - ${brand}`;
    }

    document.title = title;
  }, [route, i18n.language, t]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const target = document.getElementById(hash.slice(1));
      if (!target) return;
      requestAnimationFrame(() => {
        target.scrollIntoView();
      });
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, [route]);

  return (
    <div className="min-h-screen bg-white">
      <AnalyticsLoader enabled={analyticsAllowed} />
      <Header />
      {route === 'download' ? <DownloadPage /> : null}
      {route === 'features' ? <FeaturesPage /> : null}
      {route === 'pricing' ? <PricingPage /> : null}
      {route === 'privacy' ? <PrivacyPage /> : null}
      {route === 'terms' ? <TermsPage /> : null}
      {route === 'contact' ? <ContactPage /> : null}
      {route === 'home' ? <HomePage /> : null}
      {route !== 'home' ? <CTA /> : null}
      <Footer />
      <CookieBanner
        visible={shouldShow}
        onAccept={accept}
        onReject={reject}
        policyHref={cookiePolicyHref}
      />
    </div>
  );
}
